import { getApiBaseUrl } from '../../utils/environment';
import { authorizedFetch } from './authTransport';
import { ApiError, apiErrorFromNetworkFailure, apiErrorFromResponse } from './ApiError';

export interface LegalAcceptanceResponse {
  /** Server-recorded consent; browser-local data is not acceptance evidence. */
  accepted: boolean;
  /** Versions the server currently requires; compare them with rendered content. */
  requiredTermsVersion: string;
  requiredPrivacyVersion: string;
  /** Null until accepted; the client never invents this timestamp. */
  acceptedAt: string | null;
  /** Onboarding prerequisite reported by the server. */
  needsScreenName: boolean;
  /** Server-derived eligibility for gated actions such as joining an invite. */
  eligible: boolean;
}

/** Validate the documented response shape before exposing it as trusted app state. */
function isLegalAcceptanceResponse(value: unknown): value is LegalAcceptanceResponse {
  if (!value || typeof value !== 'object') return false;
  const response = value as Record<string, unknown>;
  return typeof response.accepted === 'boolean'
    && typeof response.requiredTermsVersion === 'string'
    && typeof response.requiredPrivacyVersion === 'string'
    && (typeof response.acceptedAt === 'string' || response.acceptedAt === null)
    && typeof response.needsScreenName === 'boolean'
    && typeof response.eligible === 'boolean';
}

async function readStatus(response: Response): Promise<LegalAcceptanceResponse> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new ApiError('The legal acceptance status response was invalid.', response.status, 'unknown');
  }
  if (!isLegalAcceptanceResponse(body)) {
    throw new ApiError('The legal acceptance status response was incomplete.', response.status, 'unknown', body);
  }
  return body;
}

/** Authenticated server authority for versioned terms and privacy acceptance. */
/**
 * Authenticated boundary for policy status and acceptance. It sends only the
 * two displayed versions; identity and acceptance time are server-derived,
 * and malformed or failed responses remain errors.
 */
export class LegalAcceptanceService {
  private readonly endpoint = `${getApiBaseUrl()}/user/legal-acceptance`;

  async getStatus(signal?: AbortSignal): Promise<LegalAcceptanceResponse> {
    let response: Response;
    try {
      response = await authorizedFetch(this.endpoint, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal,
      });
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw apiErrorFromNetworkFailure(error, 'Could not load legal acceptance status.');
    }
    if (!response.ok) throw await apiErrorFromResponse(response, 'Could not load legal acceptance status.');
    return readStatus(response);
  }

  async accept(termsVersion: string, privacyVersion: string, signal?: AbortSignal): Promise<LegalAcceptanceResponse> {
    let response: Response;
    try {
      response = await authorizedFetch(this.endpoint, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ termsVersion, privacyVersion }),
        signal,
      });
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw apiErrorFromNetworkFailure(error, 'Could not save legal acceptance.');
    }
    if (!response.ok) throw await apiErrorFromResponse(response, 'Could not save legal acceptance.');
    return readStatus(response);
  }
}

export const legalAcceptanceService = new LegalAcceptanceService();
